import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Bus, Headphones, Map, ShieldCheck, Wrench } from 'lucide-react';

const teams = [
  {
    icon: Bus,
    title: 'Operations',
    description: 'Coordinate schedules, route readiness, terminal flow, and partner communication.',
  },
  {
    icon: Headphones,
    title: 'Customer Support',
    description: 'Help passengers with bookings, trip changes, payment questions, and travel updates.',
  },
  {
    icon: Wrench,
    title: 'Fleet & Maintenance',
    description: 'Keep buses prepared, inspected, and ready for safe intercity service.',
  },
  {
    icon: Map,
    title: 'Route Planning',
    description: 'Improve route coverage, timing, stop quality, and demand visibility across cities.',
  },
];

const openings = [
  { role: 'Customer Experience Associate', type: 'Full time', location: 'Accra / Hybrid' },
  { role: 'Route Operations Coordinator', type: 'Full time', location: 'Accra' },
  { role: 'Fleet Support Officer', type: 'Contract', location: 'Kumasi' },
];

export function Careers() {
  return (
    <div className="min-h-screen bg-brand-white">
      <section className="relative overflow-hidden bg-secondary-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(245,150,30,0.28),transparent_30%),linear-gradient(135deg,#0f172a,#093A7C)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1fr_0.8fr] gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-brand-orange font-bold uppercase tracking-wider mb-4">Careers</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
              Help build Ghana's next intercity travel platform.
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed max-w-2xl">
              Join a practical, service-minded team improving booking, route visibility, and customer support for everyday travelers.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:careers@woteintercity.com"
                className="inline-flex items-center justify-center gap-2 bg-brand-orange text-white px-7 py-3 rounded-full font-bold hover:bg-brand-orange/90 transition-colors"
              >
                Apply by Email
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#openings"
                className="inline-flex items-center justify-center gap-2 border border-white/25 text-white px-7 py-3 rounded-full font-bold hover:bg-white/10 transition-colors"
              >
                View Roles
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-lg border border-white/15 bg-white/10 p-7"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-lg bg-brand-orange flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-lg">Current focus</p>
                <p className="text-blue-100 text-sm">Operations, support, and service reliability</p>
              </div>
            </div>
            <div className="space-y-4">
              {['Passenger-first decisions', 'Clear ownership', 'Safe transport standards'].map((item) => (
                <div key={item} className="flex items-center gap-3 border-t border-white/10 pt-4">
                  <ShieldCheck className="w-5 h-5 text-brand-orange" />
                  <span className="font-semibold">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-blue mb-4">Teams we are growing</h2>
            <p className="text-lg text-secondary-600 leading-relaxed">
              We look for people who care about transport details, customer clarity, and dependable day-to-day execution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teams.map((team, index) => {
              const Icon = team.icon;
              return (
                <motion.div
                  key={team.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  className="border border-secondary-100 rounded-lg p-6 bg-brand-white"
                >
                  <div className="w-12 h-12 rounded-lg bg-brand-orange/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-brand-orange" />
                  </div>
                  <h3 className="text-lg font-bold text-secondary-900 mb-3">{team.title}</h3>
                  <p className="text-secondary-600 leading-relaxed">{team.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="openings" className="py-20 bg-secondary-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-blue mb-3">Open roles</h2>
              <p className="text-secondary-600">Send your CV and the role name to careers@woteintercity.com.</p>
            </div>
            <a href="mailto:careers@woteintercity.com" className="text-brand-blue font-bold hover:text-brand-orange transition-colors">
              careers@woteintercity.com
            </a>
          </div>

          <div className="divide-y divide-secondary-200 rounded-lg border border-secondary-200 bg-white overflow-hidden">
            {openings.map((opening) => (
              <div key={opening.role} className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-secondary-900">{opening.role}</h3>
                  <p className="text-sm text-secondary-500 mt-1">{opening.type} · {opening.location}</p>
                </div>
                <a
                  href={`mailto:careers@woteintercity.com?subject=${encodeURIComponent(opening.role)}`}
                  className="inline-flex items-center gap-2 text-brand-blue font-bold hover:text-brand-orange transition-colors"
                >
                  Apply
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-blue text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-5">Not seeing the right role?</h2>
          <p className="text-lg text-blue-100 mb-8">
            Tell us where you can help: customer experience, operations, route planning, fleet support, or technology.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center justify-center gap-2 bg-white text-brand-blue px-7 py-3 rounded-full font-bold hover:bg-blue-50 transition-colors"
          >
            Learn about Wote InterCity
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
